/* eslint-disable max-lines-per-function */
import {
    type interfaces,
    ContainerModule,
} from '@patsnap/tahiti-core/shared/inversify';

import { SpecificationModel } from './models/specification-model';
import { SpecificationModelImpl } from './models/impls/specification-model';

import { SpecificationRepository } from './services/specification-repository';
import { SpecificationRepositoryImpl } from './repositories/impls/specification-repository';

import { SpecificationService } from './services/specification-service';
import { SpecificationServiceImpl } from './services/impls/specification-service';
import { SpecificationApiImpl } from './apis/impls/api';

import { SpecificationTaskCreateModel } from './models/specification-task-create-model';
import { SpecificationTaskCreateModelImpl } from './models/impls/specification-task-create-model';
import { SpecificationTaskCreateRepository } from './services/specification-task-create-repository';
import { SpecificationTaskCreateRepositoryImpl } from './repositories/impls/specification-task-create-repository';
import { SpecificationTaskCreateService } from './services/specification-task-create-service';
import { SpecificationTaskCreateServiceImpl } from './services/impls/specification-task-create-service';
import { SpecificationFeatureControlService } from './services/specification-feature-control-service';
import { SpecificationFeatureControlServiceImpl } from './services/impls/specification-feature-control-service-impl';

import { InitAiSpecificationTaskApi } from './apis/init-ai-specification-task';
import { DetectLanguageApi } from './apis/detect-language';
import { GetInputContentApi } from './apis/get-input-content';
import { SubmitConfirmDisclosureContentApi } from './apis/submit-confirm-disclosure-content';
import { GetClassificationApi } from './apis/get-classification';
import { SpecificationConfirmDisclosureService } from './services/specification-confirm-disclosure-service';
import { SpecificationConfirmDisclosureServiceImpl } from './services/impls/specification-confirm-disclosure-service';
import { SpecificationConfirmDisclosureModel } from './models/specification-confirm-disclosure-model';
import { SpecificationConfirmDisclosureModelImpl } from './models/impls/specification-confirm-disclosure-model';
import { SpecificationConfirmDisclosureRepositoryImpl } from './repositories/impls/specification-confirm-disclosure-repository';
import { SpecificationConfirmDisclosureRepository } from './services/specification-confirm-disclosure-repository';
import { GenTechnologyCategoryApi } from './apis/gen-technology-category';
import { GenTechnicalApi } from './apis/gen-technical';
import { SubmitConfirmClaimsContentApi } from './apis/submit-confirm-claims-content';
import { GenFeatureTreeApi } from './apis/gen-feature-tree';
import { FormatClaimApi } from './apis/format-claim';
import { SpecificationConfirmClaimsModelImpl } from './models/impls/specification-confirm-claims-model';
import { SpecificationConfirmClaimsModel } from './models/specification-confirm-claims-model';
import { SpecificationConfirmClaimsRepositoryImpl } from './repositories/impls/specification-confirm-claims-repository';
import { SpecificationConfirmClaimsRepository } from './services/specification-confirm-claims-repository';
import { SpecificationConfirmClaimsServiceImpl } from './services/impls/specification-confirm-claims-service';
import { SpecificationConfirmClaimsService } from './services/specification-confirm-claims-service';
import { SpecificationGenContentModelImpl } from './models/impls/specification-gen-content-model';
import { SpecificationGenContentModel } from './models/specification-gen-content-model';
import { SpecificationGenContentServiceImpl } from './services/impls/specification-gen-content-service';
import { SpecificationGenContentService } from './services/specification-gen-content-service';
import { SpecificationGenParagraphApi } from './apis/gen-specification-paragraph';
import { CommandContribution } from '@patsnap/tahiti-core';
import { ExpandContribution } from './services/impls/contributions/expand-command-contribution';
import { PolishContribution } from './services/impls/contributions/polish-command-contribution';
import { SimplifyContribution } from './services/impls/contributions/simplify-command-contribution';
import { ContinueContribution } from './services/impls/contributions/continue-command-contribution';
import { SpecificationAiOptimizeParagraphApi } from './apis/ai-optimize-specification-paragraph';
import { AddEmbodimentCommandContribution } from './services/impls/contributions/add-embodiment-command-contribution';
import { AppendEmbodimentCommandContribution } from './services/impls/contributions/append-embodiment-command-contribution';
import { ImproveEmbodimentCommandContribution } from './services/impls/contributions/improve-embodiment-command-contribution';
import { GenEmbodimentApi } from './apis/gen-embodiment-api';
import { SpecificationFiguresServiceImpl } from './services/impls/specification-figures-service';
import { SpecificationFiguresService } from './services/specification-figures-service';
import { FiguresPersistenceService } from './services/figures-persistence-service';
import { FiguresPersistenceLocalService } from './services/impls/figures-persistence-local-service';
import { ClaimsImageUploadServerService } from './services/impls/claims-image-upload-service';
import { ClaimsImageUploadService } from './services/claims-image-upload-service';
import { Specification } from './services/impls/specification';
import { FigureService } from './services/figure-service';
import { FigureOrderService } from './services/figure-order-service';
import { FigureAddService } from './services/figure-add-service';
import { FigureServiceImpl } from './services/impls/figure-service';
import { FigureOrderServiceImpl } from './services/impls/figure-order-service';
import { SpecificationDataServiceImpl } from './services/impls/specification-data-service';
import { SpecificationDataService } from './services/specification-data-service';
import { BatchUploadFiguresApi } from './apis/batch-upload-figures-api';
import { FileParseApi } from './apis/file-parse-api';
import { FileUploadApi } from './apis/file-upload-api';
import { FileDeleteApi } from './apis/file-delete-api';
import { ParsedFiguresApi } from './apis/parsed-figures-api';
import { GetFiguresApi } from './apis/get-figures';
import { GetTermsApi } from './apis/get-terms';
import { FigureAnnotationService } from '@/services/figure-annotation-service/figure-annotation-service';
import { figureAnnotationServiceFactory } from '@/services/figure-annotation-service/impls/figure-annotation-service';
import { AnnotationManagerBinding } from '@telecom/shared/utils/annotation-tool/interfaces/annotation-manager';
import { AnnotationEditorBinding, getAnnotationManager, getEditor } from './services/impls/annotation-manager';
import { TermService } from './services/term-service';
import { TermServiceImpl } from './services/impls/term-service';
import { RootSection } from './services/sections/root-section';
import { SpecificationSection } from './services/specification-section';
import { EmbodimentSection } from './services/sections/embodiment-section';
import { EmbodimentOutlineSection } from './services/sections/embodiment-outline-section';
import { TechFieldSection } from './services/sections/tech-field-section';
import { BackgroundSection } from './services/sections/background-section';
import { SummarySection } from './services/sections/summary-section';
import { AbstractSection } from './services/sections/abstract-section';
import { TitleSection } from './services/sections/title-section';
import { SpecificationEmbodimentOutlineService } from './services/specification-embodiment-outline-service';
import { SpecificationEmbodimentOutlineServiceImpl } from './services/impls/specification-embodiment-outline-service';
import { CreateEmbodimentOutlineApi } from './apis/create-embodiment-outline-api';
import { SpecificationEmbodimentOutlineModel } from './models/specification-embodiment-outline-model';
import { SpecificationEmbodimentOutlineModelImpl } from './models/impls/specification-embodiment-outline-model';
import { EmbodimentItemSection } from './services/sections/embodiment-item-section';
import { SpecificationSectionManager } from './services/specification-section-manager';
import { SpecificationSectionManagerImpl } from './services/impls/specification-section-manager';
import { EmbodimentConclusionSection } from './services/sections/embodiment-conclusion-section';
import { AbstractFigureSection } from './services/sections/abstract-figure-section';
import { FiguresSection } from './services/sections/figures-section';
import { FiguresDescriptionSection } from './services/sections/figure-description-section';
import { FileUploadServiceImpl } from './services/impls/file-upload-service';
import { FileRecognitionServiceImpl } from './services/impls/file-recognition-service';
import { FileUploadService } from './services/file-upload-service';
import { FileDeleteService } from './services/file-delete-service';
import { FileDeleteServiceImpl } from './services/impls/file-delete-service';
import { FileRecognitionService } from './services/file-recognition-service';
import { FileStorageService } from './services/file-storage-service';
import { MemoryFileStorageService } from './services/impls/file-storage-service';
import { FileValidationService } from './services/file-validation-service';
import { FileValidationServiceImpl } from './services/impls/file-validation-service';
import { FileProcessingService } from './services/file-processing-service';
import { FileProcessingServiceImpl } from './services/impls/file-processing-service';
import { ImageProviderService } from '../../presentation/pages/specification/components/confirm-claims-form/services/image-provider-service';
import { ImageProviderServiceImpl } from '../../presentation/pages/specification/components/confirm-claims-form/services/impls/image-provider-service';
import { ImageSaveService } from '../../presentation/pages/specification/components/confirm-claims-form/services/image-save-service';
import { ImageSaveServiceImpl } from '../../presentation/pages/specification/components/confirm-claims-form/services/impls/image-save-service';
import { ImageSelectorCommandContribution } from '../../presentation/pages/specification/components/confirm-claims-form/contributions/image-selector-command-contribution';
import { WireFrameExtractApi, SaveWireFrameChoiceApi } from './apis/wire-frame-extract-api';
import { SpecificationFigureService } from './services/specification-figure-service';
import { DeleteFigureApi } from './apis/delete-figure-api';
import { ReorderFiguresApi } from './apis/reorder-figures-api';
import { SpecificationStorageService, SpecificationStorageServiceImpl } from './services/specification-storage-service';

export const specificationModule = new ContainerModule((bind: interfaces.Bind) => {
    // contributions
    bind(CommandContribution).to(ExpandContribution);
    bind(CommandContribution).to(PolishContribution);
    bind(CommandContribution).to(SimplifyContribution);
    bind(CommandContribution).to(ContinueContribution);
    bind(CommandContribution).to(AddEmbodimentCommandContribution);
    bind(CommandContribution).to(AppendEmbodimentCommandContribution);
    bind(CommandContribution).to(ImproveEmbodimentCommandContribution);
    bind(CommandContribution).to(ImageSelectorCommandContribution);

    // Bind APIs
    bind(SpecificationApiImpl).toSelf().inSingletonScope();
    bind(InitAiSpecificationTaskApi).toService(SpecificationApiImpl);
    bind(DetectLanguageApi).toService(SpecificationApiImpl);
    bind(GetInputContentApi).toService(SpecificationApiImpl);
    bind(SubmitConfirmDisclosureContentApi).toService(SpecificationApiImpl);
    bind(GetClassificationApi).toService(SpecificationApiImpl);
    bind(GenTechnologyCategoryApi).toService(SpecificationApiImpl);
    bind(GenTechnicalApi).toService(SpecificationApiImpl);
    bind(GenEmbodimentApi).toService(SpecificationApiImpl);
    bind(SubmitConfirmClaimsContentApi).toService(SpecificationApiImpl);
    bind(GenFeatureTreeApi).toService(SpecificationApiImpl);
    bind(FormatClaimApi).toService(SpecificationApiImpl);
    bind(SpecificationGenParagraphApi).toService(SpecificationApiImpl);
    bind(SpecificationAiOptimizeParagraphApi).toService(SpecificationApiImpl);
    bind(BatchUploadFiguresApi).toService(SpecificationApiImpl);
    bind(FileParseApi).toService(SpecificationApiImpl);
    bind(FileUploadApi).toService(SpecificationApiImpl);
    bind(FileDeleteApi).toService(SpecificationApiImpl);
    bind(ParsedFiguresApi).toService(SpecificationApiImpl);
    bind(GetFiguresApi).toService(SpecificationApiImpl);
    bind(GetTermsApi).toService(SpecificationApiImpl);
    bind(CreateEmbodimentOutlineApi).toService(SpecificationApiImpl);
    bind(WireFrameExtractApi).toService(SpecificationApiImpl);
    bind(SaveWireFrameChoiceApi).toService(SpecificationApiImpl);
    bind(DeleteFigureApi).toService(SpecificationApiImpl);
    bind(ReorderFiguresApi).toService(SpecificationApiImpl);
    // Bind Models
    bind(SpecificationModel).to(SpecificationModelImpl);
    bind(SpecificationTaskCreateModel).to(SpecificationTaskCreateModelImpl);
    bind(SpecificationConfirmDisclosureModel).to(SpecificationConfirmDisclosureModelImpl);
    bind(SpecificationConfirmClaimsModel).to(SpecificationConfirmClaimsModelImpl);
    bind(SpecificationGenContentModel).to(SpecificationGenContentModelImpl);
    bind(SpecificationEmbodimentOutlineModel).to(SpecificationEmbodimentOutlineModelImpl);
    // Bind Repositories
    bind(SpecificationRepository).to(SpecificationRepositoryImpl);
    bind(SpecificationTaskCreateRepository).to(SpecificationTaskCreateRepositoryImpl);
    bind(SpecificationConfirmDisclosureRepository).to(SpecificationConfirmDisclosureRepositoryImpl);
    bind(SpecificationConfirmClaimsRepository).to(SpecificationConfirmClaimsRepositoryImpl);

    // Bind Services
    bind(SpecificationService).to(SpecificationServiceImpl);
    bind(SpecificationTaskCreateService).to(SpecificationTaskCreateServiceImpl);
    bind(SpecificationConfirmDisclosureService).to(SpecificationConfirmDisclosureServiceImpl);
    bind(SpecificationConfirmClaimsService).to(SpecificationConfirmClaimsServiceImpl);
    bind(SpecificationGenContentService).to(SpecificationGenContentServiceImpl);
    bind(SpecificationFiguresService).to(SpecificationFiguresServiceImpl);
    bind(SpecificationFeatureControlService).to(SpecificationFeatureControlServiceImpl);
    bind(FiguresPersistenceService).to(FiguresPersistenceLocalService);
    bind(SpecificationStorageService).to(SpecificationStorageServiceImpl);
    bind(ClaimsImageUploadService).to(ClaimsImageUploadServerService);
    bind(Specification).toSelf();
    bind(SpecificationFigureService).toService(Specification);
    bind(FigureServiceImpl).toSelf();
    bind(FigureService).toService(FigureServiceImpl);
    bind(FigureAddService).toService(FigureServiceImpl);
    bind(FigureOrderServiceImpl).toSelf();
    bind(FigureOrderService).toService(FigureOrderServiceImpl);
    bind(SpecificationDataService).to(SpecificationDataServiceImpl);
    bind(FigureAnnotationService).toDynamicValue(figureAnnotationServiceFactory);
    bind(AnnotationManagerBinding).toDynamicValue(getAnnotationManager).inTransientScope();
    bind(AnnotationEditorBinding).toDynamicValue(getEditor).inTransientScope();
    bind(TermService).to(TermServiceImpl);

    // Bind Sections
    bind(SpecificationSectionManager).to(SpecificationSectionManagerImpl);
    bind(RootSection).toSelf();
    bind(SpecificationSection).to(TitleSection);
    bind(SpecificationSection).to(TechFieldSection);
    bind(SpecificationSection).to(BackgroundSection);
    bind(SpecificationSection).to(SummarySection);
    bind(SpecificationSection).to(EmbodimentOutlineSection);
    bind(SpecificationSection).to(EmbodimentSection);
    bind(SpecificationSection).to(AbstractSection);
    bind(SpecificationSection).to(AbstractFigureSection);
    bind(SpecificationSection).to(FiguresSection);
    bind(SpecificationSection).to(FiguresDescriptionSection);
    bind(SpecificationSection).to(EmbodimentItemSection);
    bind(SpecificationSection).to(EmbodimentConclusionSection);
    bind(EmbodimentItemSection).toSelf();
    bind(EmbodimentConclusionSection).toSelf();

    // Bind Section Services
    bind(SpecificationEmbodimentOutlineService).to(SpecificationEmbodimentOutlineServiceImpl);

    // Bind File Upload Services
    bind(FileUploadService).to(FileUploadServiceImpl);
    bind(FileDeleteService).to(FileDeleteServiceImpl);
    bind(FileRecognitionService).to(FileRecognitionServiceImpl);
    bind(FileStorageService).to(MemoryFileStorageService);
    bind(FileValidationService).to(FileValidationServiceImpl);
    bind(FileProcessingService).to(FileProcessingServiceImpl).inTransientScope();

    // Bind Image Selection Services
    bind(ImageProviderService).to(ImageProviderServiceImpl);
    bind(ImageSaveService).to(ImageSaveServiceImpl);
});
